import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { MoreHorizontal } from "lucide-react";
import clsx from "clsx";

export interface CardMenuAction {
  key: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Removing actions are drawn in red. */
  critical?: boolean;
}

interface Props {
  actions: CardMenuAction[];
  label: string;
}

/** The «⋯» of the card panel header, drawn below 768px: the secondary actions the row has no room for. */
export default function DeskCardMoreMenu({ actions, label }: Props) {
  return (
    <Menu>
      <MenuButton
        aria-label={label}
        title={label}
        className="flex h-11 w-11 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-300 dark:hover:bg-zinc-900 md:hidden"
      >
        <MoreHorizontal className="h-5 w-5" aria-hidden />
      </MenuButton>
      {/* z-[80] keeps the menu above the panel (z-50) and its attachment preview (z-70).
          Escape and Tab inside the open menu never bubble to window (Headless UI stops them),
          so the panel's own key handler leaves them alone. */}
      <MenuItems
        anchor="bottom end"
        modal={false}
        className="z-[80] w-64 rounded-xl border border-zinc-200 bg-white py-1 text-sm shadow-lg outline-none [--anchor-gap:6px] dark:border-zinc-700 dark:bg-zinc-900"
      >
        {actions.map((action) => (
          <MenuItem key={action.key} disabled={action.disabled}>
            <button
              type="button"
              onClick={action.onClick}
              className={clsx(
                "block min-h-11 w-full px-4 text-left data-[focus]:bg-zinc-50 data-[disabled]:opacity-40 dark:data-[focus]:bg-zinc-800",
                action.critical ? "text-red-600 dark:text-red-400" : "text-zinc-800 dark:text-zinc-200"
              )}
            >
              {action.label}
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}
